/* eslint-disable */
// Event "Details" card with admin-editable RichTextEditor / read-only
// DOMPurify-sanitised render (iter-126g, slice 6 of EventDetail.jsx refactor).
//
// One card, two modes:
//   - Admin → RichTextEditor with autosave-on-blur.
//   - Non-admin → DOMPurify-sanitised HTML with brand-yellow underlined
//     hyperlinks (event-description class).
//
// The iter-123 DOMPurify hook that forces every <a> to carry
// `target="_blank"` and `rel="noopener noreferrer"` is registered ONCE
// at module load. Importing this file is enough to register it.
import React from "react";
import DOMPurify from "dompurify";
import { FileText } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import RichTextEditor from "../../components/RichTextEditor";


// iter-123: Force every link in user-authored event descriptions to open in
// a new tab + carry rel="noopener noreferrer". Registered once on import.
if (typeof DOMPurify.addHook === "function") {
  DOMPurify.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName === "A") {
      node.setAttribute("target", "_blank");
      node.setAttribute("rel", "noopener noreferrer");
    }
  });
}


export const EventDescriptionView = ({
  description,
  showAdminControls,
  detailsDraft,
  setDetailsDraft,
  detailsDirty,
  setDetailsDirty,
  detailsSaving,
  detailsSavedAt,
  handleDetailsBlur,
}) => {
  // Hide the whole card for non-admins when there's nothing to read.
  if (!showAdminControls && !description) return null;

  return (
    <Card className="glass-card" data-testid="event-details-card">
      <CardHeader className="border-b border-white/5 pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            <CardTitle className="font-heading text-lg">Details</CardTitle>
          </div>
          {showAdminControls && (
            <div className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="details-save-status">
              {detailsSaving ? (
                <span className="text-amber-300">Saving…</span>
              ) : detailsDirty ? (
                <span>Unsaved changes — click away to save</span>
              ) : detailsSavedAt ? (
                <span className="text-green-400">Saved ✓</span>
              ) : (
                <span>Click in the box and start typing — autosaves on blur</span>
              )}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-4">
        {showAdminControls ? (
          <RichTextEditor
            value={detailsDraft}
            onChange={(html) => {
              setDetailsDraft(html);
              setDetailsDirty(true);
            }}
            onBlur={handleDetailsBlur}
            placeholder="Add event details, set list notes, what to bring, dress code…"
            minHeight={120}
            maxHeight={420}
            compact
            testId="event-details-editor"
          />
        ) : (
          <div
            className="event-description text-sm text-muted-foreground max-w-none break-words"
            data-testid="event-details-readonly"
            /* Defence-in-depth XSS guard: backend already bleach-sanitises
               on write (see routes/events.py _sanitize_event_html), but
               DOMPurify on render protects against any future write path
               that forgets to sanitise + against direct-DB tampering.
               iter-126b: the .event-description CSS class makes
               hyperlinks visible (primary colour + underline) — replaces
               the broken iter-123 prose-a:* modifiers that required the
               @tailwindcss/typography plugin (not installed). */
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(description || "", {
                USE_PROFILES: { html: true },
                ADD_ATTR: ["target", "rel"],
              }),
            }}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default EventDescriptionView;
